import { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { signUp } from '../lib/auth';
import { supabase } from '../lib/supabase';
import '../components/auth/AuthStyles.css';

export default function Signup() {
  const navigate = useNavigate();
  const { inviteCode } = useParams();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState(inviteCode ? 'client' : 'practitioner');
  const [invite, setInvite] = useState(inviteCode || '');
  const [practiceName, setPracticeName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // If user arrives via invite link, lock to client role
  const isInvited = !!inviteCode;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Basic validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      setLoading(false);
      return;
    }

    // If client, verify invite code or email exists
    if (role === 'client') {
      if (invite) {
        const { data: clientRecord, error: lookupError } = await supabase
          .from('clients')
          .select('id, practitioner_id')
          .eq('invite_code', invite)
          .single();

        if (lookupError || !clientRecord) {
          setError('Invalid invite code. Please check with your practitioner.');
          setLoading(false);
          return;
        }
      } else {
        // Check if a client record exists with this email
        const { data: clientRecord, error: lookupError } = await supabase
          .from('clients')
          .select('id')
          .eq('email', email)
          .is('auth_user_id', null)
          .single();

        if (lookupError || !clientRecord) {
          setError('No account found for this email. Ask your practitioner to add you first.');
          setLoading(false);
          return;
        }
      }
    }

    try {
      const metadata = { name };
      if (role === 'practitioner') {
        metadata.practice_name = practiceName;
      }
      if (role === 'client' && invite) {
        metadata.invite_code = invite;
      }

      const { data, error: authError } = await signUp(email, password, role, metadata);

      if (authError) {
        setError(authError.message);
        setLoading(false);
        return;
      }

      // If practitioner, create their practitioner record
      if (role === 'practitioner' && data?.user) {
        await supabase.from('practitioners').insert({
          auth_user_id: data.user.id,
          name,
          email,
          practice_name: practiceName,
        });
      }

      // If client, link auth user to existing client record by email or invite code
      if (role === 'client' && data?.user) {
        // Try matching by invite code first, then by email
        if (invite) {
          await supabase
            .from('clients')
            .update({ auth_user_id: data.user.id })
            .eq('invite_code', invite);
        } else {
          await supabase
            .from('clients')
            .update({ auth_user_id: data.user.id })
            .eq('email', email)
            .is('auth_user_id', null);
        }
      }

      // Supabase may require email confirmation — check if session exists
      if (data?.session) {
        if (role === 'client') {
          navigate('/client', { replace: true });
        } else {
          navigate('/practitioner', { replace: true });
        }
      } else {
        // Email confirmation required
        setSuccess(true);
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="auth-screen">
        <div className="auth-brand">
          <img src="/correly-icon.svg" alt="Correly" className="auth-brand-icon" />
          <h1>Correly</h1>
        </div>
        <div className="auth-card" style={{ textAlign: 'center' }}>
          <h2>Check your email</h2>
          <p style={{ color: 'var(--color-text-secondary)', lineHeight: 1.6 }}>
            We sent a confirmation link to <strong>{email}</strong>. Click the link to activate your account, then come back here to sign in.
          </p>
          <Link to="/login" className="auth-submit" style={{ display: 'block', marginTop: '24px', textDecoration: 'none', textAlign: 'center' }}>
            Back to Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-screen">
      <div className="auth-brand">
        <img src="/correly-icon.svg" alt="Correly" className="auth-brand-icon" />
        <h1>Correly</h1>
        <p className="tagline">Correlation over Guesswork.</p>
      </div>

      <div className="auth-card">
        <h2>{isInvited ? 'Join Your Practitioner' : 'Create Your Account'}</h2>

        <form className="auth-form" onSubmit={handleSubmit}>
          {error && <div className="auth-error">{error}</div>}

          {/* Role selector — hidden if arriving via invite */}
          {!isInvited && (
            <div className="form-group">
              <label>I am a...</label>
              <div className="role-selector">
                <div
                  className={`role-option ${role === 'practitioner' ? 'selected' : ''}`}
                  onClick={() => setRole('practitioner')}
                >
                  <div className="role-icon">🩺</div>
                  <div className="role-label">Practitioner</div>
                  <div className="role-desc">I manage clients</div>
                </div>
                <div
                  className={`role-option ${role === 'client' ? 'selected' : ''}`}
                  onClick={() => setRole('client')}
                >
                  <div className="role-icon">🌿</div>
                  <div className="role-label">Client</div>
                  <div className="role-desc">I have an invite</div>
                </div>
              </div>
            </div>
          )}

          <div className="form-group">
            <label htmlFor="name">Full Name</label>
            <input
              id="name"
              type="text"
              className="form-input"
              placeholder="Your full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              autoComplete="name"
            />
          </div>

          <div className="form-group">
            <label htmlFor="signup-email">Email</label>
            <input
              id="signup-email"
              type="email"
              className="form-input"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </div>

          <div className="form-group">
            <label htmlFor="signup-password">Password</label>
            <input
              id="signup-password"
              type="password"
              className="form-input"
              placeholder="At least 6 characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={6}
              autoComplete="new-password"
            />
          </div>

          {/* Practice name for practitioners */}
          {role === 'practitioner' && (
            <div className="form-group">
              <label htmlFor="practice-name">Practice Name <span style={{ color: 'var(--color-text-muted)', fontWeight: 400 }}>(optional)</span></label>
              <input
                id="practice-name"
                type="text"
                className="form-input"
                placeholder="e.g. Harmony Wellness"
                value={practiceName}
                onChange={(e) => setPracticeName(e.target.value)}
                autoComplete="organization"
              />
            </div>
          )}

          {/* Invite code for clients */}
          {role === 'client' && !isInvited && (
            <div className="form-group">
              <label htmlFor="invite-code">Invite Code</label>
              <input
                id="invite-code"
                type="text"
                className="form-input"
                placeholder="Paste the code from your practitioner"
                value={invite}
                onChange={(e) => setInvite(e.target.value)}
                required
              />
            </div>
          )}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? 'Creating account...' : 'Create Account'}
          </button>
        </form>

        <div className="auth-footer">
          Already have an account? <Link to="/login">Sign in</Link>
        </div>
      </div>
    </div>
  );
}
